/* =============================================================================
   VIEWS / ADMIN / RESERVATION-CHECKIN — „✅ Gäste sind da" (S239, Tisch-QR Cut A0)
   =============================================================================
   JONAS-MODELL (final, 2026-08-26): Die Reservierung blockt den Tisch wie bisher.
   Tippt der Service „Gäste sind da", entsteht AN DER RESERVIERUNG eine Belegung —
   ab da läuft alles wie bei einem Walk-In: Überfällig-Uhr, Auto-Release,
   Bestellen (Runden), Kassieren, Freigabe. „No-Show" gibt den Tisch wie heute
   frei (DB-Trigger `trg_reservation_end_occupancy`, damit auch der No-Show von
   einem anderen Gerät die Belegung beendet).

   Die Reservierung schließt weiterhin NUR über die Bezahlung — es gibt KEINEN
   neuen Reservierungs-Status und keinen Abschluss-Button
   (Memory project_gg_reservation_completion_model). Der Check-in setzt
   ausschließlich `reservations.arrived_at`; der Status bleibt `confirmed`, damit
   Mail-Trigger und Counter unberührt bleiben (alle Reservierungs-Mail-Trigger
   verlangen einen echten Status-Wechsel — live geprüft, S239).

   Server-Wahrheit: RPC `checkin_reservation` (phase_171) — idempotent, guards
   confirmed/heute/Tisch zugewiesen, `assert_operator_role(['manager','service'])`.
   Dieses Modul ist nur die Hand am Knopf.
============================================================================ */

import { showToast, todayISO } from '../../utils/helpers.js';
import { closeModal, rerenderActiveTab } from '../../utils/dom.js';

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Die aktive Check-in-Belegung einer Reservierung (oder null).
 * @param {string} reservationId
 * @param {Array} occupancies  `state.tableOccupancies`
 */
export function findCheckinOccupancy(reservationId, occupancies) {
  if (!reservationId) return null;
  return (occupancies || []).find(o =>
    o && o.reservationId === reservationId && o.status === 'active' && !o.deletedAt
  ) || null;
}

/**
 * Button-State für den Check-in-Einstieg. Pure — kein DOM.
 *
 * Sichtbar nur bei **bestätigter Reservierung von HEUTE** (dieselben Guards wie
 * die RPC — eine Reservierung für nächsten Freitag darf keine Belegung „jetzt"
 * erzeugen; genau das war der S119-Phantom-Walk-In).
 * Nach dem Check-in wechselt der Button auf „Belegung öffnen".
 *
 * @param {object} reservation
 * @param {object|null} occupancy  aktive Check-in-Belegung (findCheckinOccupancy)
 * @param {{today?: string}} [opts]
 * @returns {{show:boolean, mode:'checkin'|'occupancy', label:string, occupancyId:string|null}}
 */
export function reservationCheckinButton(reservation, occupancy, opts = {}) {
  const none = { show: false, mode: 'checkin', label: '', occupancyId: null };
  if (!reservation || !reservation.id) return none;
  if (occupancy && occupancy.id) {
    return { show: true, mode: 'occupancy', label: '🪑 Belegung öffnen', occupancyId: occupancy.id };
  }
  if (reservation.status !== 'confirmed') return none;
  const today = opts.today || todayISO();
  if (reservation.date !== today) return none;
  if (!Array.isArray(reservation.assignedTables) || reservation.assignedTables.length === 0) return none;
  return { show: true, mode: 'checkin', label: '✅ Gäste sind da', occupancyId: null };
}

/**
 * S240-HF6 — Button-State für „No-Show". Pure — kein DOM.
 *
 * Jonas-Entscheid (2026-08-26): No-Show gibt es nur für Reservierungen von HEUTE
 * oder aus der VERGANGENHEIT. „Nicht erschienen" ist eine Feststellung über einen
 * Zeitpunkt, der schon da war — für morgen ist sie sinnlos und war im Retest ein
 * scharfer Knopf direkt neben dem Löschen (die alte Listen-Sicht kannte diese
 * Grenze nicht, weil dort nur der Status zählte).
 *
 * Bewusst KEIN Bezug auf die Uhrzeit: eine Reservierung um 19:00, die um 19:05
 * nicht da ist, muss der Service um 19:05 markieren können — der Tag ist die
 * richtige Granularität.
 *
 * @param {object} reservation
 * @param {{today?: string, featureEnabled?: boolean}} [opts]
 * @returns {{show:boolean, mode:'noshow'|'revert', label:string}}
 */
export function reservationNoShowButton(reservation, opts = {}) {
  const none = { show: false, mode: 'noshow', label: '' };
  if (!reservation || !reservation.id) return none;
  if (opts.featureEnabled === false) return none;

  // Bereits markiert → der Weg zurück bleibt immer offen (Fehlklick-Korrektur),
  // unabhängig vom Datum.
  if (reservation.status === 'no-show') {
    return { show: true, mode: 'revert', label: 'No-Show aufheben' };
  }
  if (reservation.status !== 'confirmed') return none;

  const today = opts.today || todayISO();
  if (!reservation.date || reservation.date > today) return none;   // morgen + später
  return { show: true, mode: 'noshow', label: 'No-Show' };
}

/** Fehler der RPC in einen verständlichen Satz übersetzen. Pure. */
export function checkinErrorMessage(err) {
  const raw = (err && (err.message || err.msg)) || String(err || '');
  // S239-HF2: fremde aktive Belegung (anderer Check-in ODER Walk-In) hält den
  // Tisch. Der Tischname steckt in der Server-Meldung — mit ausgeben, sonst muss
  // der Service raten, welcher der kombinierten Tische gemeint ist.
  if (raw.includes('TABLE_OCCUPIED')) {
    const m = /TABLE_OCCUPIED:\s*Tisch\s+(.+?)\s+ist noch belegt/.exec(raw);
    const tbl = m ? m[1] : null;
    return (tbl ? `Tisch ${tbl} ist noch belegt` : 'Der Tisch ist noch belegt')
      + ' — bitte zuerst freigeben oder die Tische der Reservierung ändern.';
  }
  if (raw.includes('RESERVATION_NOT_CONFIRMED')) return 'Nur bestätigte Reservierungen können einchecken.';
  if (raw.includes('RESERVATION_NOT_TODAY'))     return 'Diese Reservierung ist nicht für heute.';
  if (raw.includes('RESERVATION_WITHOUT_TABLE')) return 'Der Reservierung ist noch kein Tisch zugewiesen.';
  if (raw.includes('RESERVATION_NOT_FOUND'))     return 'Reservierung nicht gefunden.';
  if (raw.includes('RESERVATION_DELETED'))       return 'Diese Reservierung wurde gelöscht.';
  return 'Check-in fehlgeschlagen: ' + raw;
}

/* -------------------------------------------------------------------------- */
/* Aktionen                                                                    */
/* -------------------------------------------------------------------------- */

function _state() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
    || { reservations: [], tableOccupancies: [] };
}

/**
 * Check-in ausführen. Idempotent (die RPC liefert bei erneutem Aufruf die
 * bestehende Belegung), darum ist ein Doppelklick unkritisch.
 * @returns {Promise<object|null>} die Belegung (oder null bei Fehler)
 */
export async function checkinReservation(reservationId) {
  const adapter = window.App && window.App.storage && window.App.storage.tableOccupancies;
  if (!adapter || typeof adapter.checkinReservation !== 'function') {
    showToast('Check-in nicht verfügbar (Adapter fehlt).', 'error');
    return null;
  }
  try {
    const occ = await adapter.checkinReservation(reservationId);
    const res = (_state().reservations || []).find(r => r.id === reservationId);
    showToast(`${(res && res.name) || 'Gäste'} sind da — Tisch ist jetzt belegt.`, 'success');
    if (typeof window.audit === 'function') {
      window.audit('reservation.checked_in_ui', {
        reservation_id: reservationId,
        occupancy_id: occ && occ.id
      });
    }
    closeModal();
    rerenderActiveTab();
    return occ;
  } catch (e) {
    console.error('[checkinReservation]', e);
    // OPERATOR_REQUIRED öffnet global das PIN-Overlay (_throwWriteError).
    if (!(e && e.operatorRequired)) showToast(checkinErrorMessage(e), 'error');
    return null;
  }
}

/**
 * Check-in aus dem Belegungsplan/Modal heraus und danach direkt die
 * Belegungs-Ansicht öffnen (ein Klick statt zwei).
 */
export async function checkinReservationAndOpen(reservationId) {
  const occ = await checkinReservation(reservationId);
  if (occ && occ.id && typeof window.openOccupancyWalkInModal === 'function') {
    window.openOccupancyWalkInModal(occ.id);
  }
}

/**
 * S239 Cut A0 — Auto-Check-in vor einer Bestellaufnahme aus der Reservierung
 * („Bestellung aufnehmen" heißt: die Gäste sitzen da).
 *
 * BEWUSST tolerant: schlägt die RPC fehl (Reservierung für die Zukunft, noch
 * kein Tisch zugewiesen, Rolle fehlt), läuft die Bestellung wie bisher OHNE
 * Belegung weiter — der S119-Guard bleibt damit für Vorbestellungen kommender
 * Tage intakt, statt die Bestellaufnahme zu blockieren.
 *
 * @returns {Promise<string|null>} occupancy_id der Check-in-Belegung oder null
 */
export async function ensureReservationCheckedIn(reservationId) {
  if (!reservationId) return null;
  const existing = findCheckinOccupancy(reservationId, _state().tableOccupancies);
  if (existing) return existing.id;

  const res = (_state().reservations || []).find(r => r.id === reservationId);
  // Vorprüfung im Client, damit für eine Zukunfts-Reservierung gar kein
  // (garantiert scheiterndes) RPC-Roundtrip losläuft.
  const btn = reservationCheckinButton(res, null);
  if (!btn.show || btn.mode !== 'checkin') return null;

  const adapter = window.App && window.App.storage && window.App.storage.tableOccupancies;
  if (!adapter || typeof adapter.checkinReservation !== 'function') return null;
  try {
    const occ = await adapter.checkinReservation(reservationId);
    return (occ && occ.id) || null;
  } catch (e) {
    console.warn('[ensureReservationCheckedIn] übersprungen (nicht-fatal):', e && (e.message || e));
    return null;
  }
}

// window-Bindings für die Inline-Handler (onclick, ohne `window.`-Prefix —
// dom-bindings-Parser/CSP).
if (typeof window !== 'undefined') {
  Object.assign(window, {
    checkinReservation,
    checkinReservationAndOpen
  });
}
